import React, { useState, useEffect } from 'react';
import './Applications.css';
import Footer from '../../components/Footer/Footer';
import { fetchApplications } from '../../services/api';

const Applications = () => {
  const [currentLang, setCurrentLang] = useState(() => {
    return localStorage.getItem('selectedLanguage') || 'KU';
  });
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedLang = localStorage.getItem('selectedLanguage') || 'KU';
    setCurrentLang(savedLang);

    const handleLanguageChange = (event) => {
      setCurrentLang(event.detail);
      localStorage.setItem('selectedLanguage', event.detail);
    };
    window.addEventListener('languageChange', handleLanguageChange);
    return () => window.removeEventListener('languageChange', handleLanguageChange);
  }, []);

  useEffect(() => {
    const getApplications = async () => {
      setLoading(true);
      try {
        const data = await fetchApplications();
        // Transform API data to match component's expected format
        const transformedApps = data.map(app => ({
          id: app.id,
          image: app.image || '',
          EN: {
            name: app.name?.EN || '',
            description: app.description?.EN || '',
            appStoreLink: app.appStoreLink || '',
            playStoreLink: app.playStoreLink || ''
          },
          KU: {
            name: app.name?.KU || '',
            description: app.description?.KU || '',
            appStoreLink: app.appStoreLink || '',
            playStoreLink: app.playStoreLink || ''
          },
          AR: {
            name: app.name?.AR || '',
            description: app.description?.AR || '',
            appStoreLink: app.appStoreLink || '',
            playStoreLink: app.playStoreLink || ''
          },
          TR: {
            name: app.name?.TR || '',
            description: app.description?.TR || '',
            appStoreLink: app.appStoreLink || '',
            playStoreLink: app.playStoreLink || ''
          }
        }));
        setApps(transformedApps);
      } catch (error) {
        console.error("Failed to fetch applications:", error);
        setApps([]); // Fallback to empty array on error
      } finally {
        setLoading(false);
      }
    };
    getApplications();
  }, []);

  const content = {
    EN: {
      title: 'Our Applications',
      subtitle: 'Discover our mobile applications designed to enhance your spiritual journey and daily Islamic practices.',
      downloadOn: 'Download on',
      appStore: 'App Store',
      playStore: 'Play Store',
      comingSoon: 'Coming Soon'
    },
    KU: {
      title: 'بەرنامەکانمان',
      subtitle: 'بەرنامەکانی مۆبایلی ئێمە بزانە کە دروستکراون بۆ باشترکردنی گەشتی ئاینی و کارەکانی ڕۆژانەی ئیسلامیت.',
      downloadOn: 'داگرتن لە',
      appStore: 'ئەپ ستۆر',
      playStore: 'پلەی ستۆر',
      comingSoon: 'بەزووان دێت'
    },
    AR: {
      title: 'تطبيقاتنا',
      subtitle: 'اكتشف تطبيقاتنا المحمولة المصممة لتعزيز رحلتك الروحية وممارساتك الإسلامية اليومية.',
      downloadOn: 'تحميل من',
      appStore: 'متجر التطبيقات',
      playStore: 'متجر جوجل بلاي',
      comingSoon: 'قريباً'
    },
    TR: {
      title: 'Uygulamalarımız',
      subtitle: 'Manevi yolculuğunuzu ve günlük İslami uygulamalarınızı geliştirmek için tasarlanmış mobil uygulamalarımızı keşfedin.',
      downloadOn: 'İndir',
      appStore: 'App Store',
      playStore: 'Play Store',
      comingSoon: 'Yakında'
    }
  };

  const pageContent = content[currentLang] || content.KU;

  return (
    <div className="applications-page">
      <div className="container">
        <div className="applications-content">
          {/* Title Section */}
          <div className="applications-title-section">
            <h1 className="page-title">{pageContent.title}</h1>
            <p className="page-subtitle">{pageContent.subtitle}</p>
          </div>

          {/* Apps Grid */}
          {loading ? (
            <div className="loading-message">Loading applications...</div>
          ) : apps.length === 0 ? (
            <div className="no-content-message">No applications found.</div>
          ) : (
            <div className="apps-grid">
              {apps.map((app) => {
                const appContent = app[currentLang] || app.EN;
                return (
                  <div key={app.id} className="app-card">
                    {/* App Image */}
                    <div className="app-image-container">
                      {app.image ? (
                        <img 
                          src={app.image} 
                          alt={appContent.name || 'Application'}
                          className="app-image"
                          onError={(e) => {
                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22400%22 height=%22225%22%3E%3Crect fill=%22%23e5e7eb%22 width=%22400%22 height=%22225%22/%3E%3Ctext fill=%22%239ca3af%22 font-family=%22sans-serif%22 font-size=%2214%22 dy=%2210.5%22 font-weight=%22bold%22 x=%2250%25%22 y=%2250%25%22 text-anchor=%22middle%22%3ENo Image%3C/text%3E%3C/svg%3E';
                          }}
                        />
                      ) : (
                        <div className="app-image-placeholder">
                          <span>No Image</span>
                        </div>
                      )}
                      <div className="app-image-overlay"></div>
                    </div>

                    {/* App Content */}
                    <div className="app-content">
                      <h2 className="app-name">{appContent.name || 'Untitled Application'}</h2>
                      <p className="app-description">{appContent.description || 'No description available.'}</p>

                      {/* Download Buttons */}
                      <div className="app-downloads">
                        {appContent.appStoreLink && (
                          <a
                            href={appContent.appStoreLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="download-button app-store-button"
                            aria-label={`${pageContent.downloadOn} ${pageContent.appStore}`}
                          >
                            <svg className="store-icon" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.12997 6.91 8.81997 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z"/>
                            </svg>
                            <div className="button-text">
                              <span className="button-label">{pageContent.downloadOn}</span>
                              <span className="button-store">{pageContent.appStore}</span>
                            </div>
                          </a>
                        )}

                        {appContent.playStoreLink && (
                          <a
                            href={appContent.playStoreLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="download-button play-store-button"
                            aria-label={`${pageContent.downloadOn} ${pageContent.playStore}`}
                          >
                            <svg className="store-icon" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                            </svg>
                            <div className="button-text">
                              <span className="button-label">{pageContent.downloadOn}</span>
                              <span className="button-store">{pageContent.playStore}</span>
                            </div>
                          </a>
                        )}

                        {!appContent.appStoreLink && !appContent.playStoreLink && (
                          <div className="coming-soon-badge">{pageContent.comingSoon}</div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Applications;

